import { Checkbox, Chip, MenuItem } from '@mui/material';
import { FieldTemplate } from 'api/models';
import { getFieldLabel } from '../../utils';
import { useTranslation } from 'react-i18next';
import ClearableSelect, {
  ClearableSelectProps,
} from 'components/ClearableSelect/ClearableSelect';
import { FieldAttributesOptions } from 'api/models/FieldTemplate';
import { useEffect, useState } from 'react';
import { GeneralIds, ModelTypes } from 'types/types';
import { useParams } from 'react-router-dom';
import { query } from 'operations';
import { useRecoilValue } from 'recoil';
import { fieldTemplatesState } from 'state';
import { FieldHelperText } from './FieldHelperText';

type FieldProps = ClearableSelectProps & {
  field: FieldTemplate;
  isImported?: boolean;
};

export function SelectField({
  disabled,
  field,
  error,
  value,
  onChange,
  helperText,
  name,
  onClear,
  inputProps,
  sx,
  isImported,
}: FieldProps) {
  const {
    isMultiSelect,
    description,
    defaultValue,
    optionsSourceFieldTemplateId,
  } = field.fieldAttributes ?? {};
  const { t } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  // only used for tracking changes so we fetch the options if they are edited within the same field
  const formsFields = useRecoilValue(fieldTemplatesState);
  const [options, setOptions] = useState<FieldAttributesOptions[]>();

  useEffect(() => {
    setOptions(field.fieldAttributes?.options ?? []);
    async function fetchData() {
      if (!optionsSourceFieldTemplateId)
        return setOptions(field?.fieldAttributes.options);
      const { data: fields } = await query<FieldTemplate[]>({
        type: ModelTypes.fieldTemplate,
        ids: {
          projectId,
          id: optionsSourceFieldTemplateId,
        },
      });

      const el = fields[0];
      setOptions(el?.fieldAttributes.options);
    }
    fetchData();
  }, [
    optionsSourceFieldTemplateId,
    field.fieldAttributes?.options,
    field.fieldAttributes?.referedFieldsOptions,
    formsFields,
    projectId,
  ]);

  const val =
    // @ts-ignore
    (!value || value?.length === 0) && value !== null ? defaultValue : value;

  return (
    <>
      <ClearableSelect
        inputProps={inputProps}
        name={name}
        disabled={disabled}
        fullWidth
        label={getFieldLabel({ field, t })}
        isClearable={true}
        onClear={onClear}
        value={val}
        multiple={isMultiSelect ? true : false}
        renderValue={selected => {
          const selectedValues = Array.isArray(selected)
            ? selected
            : [selected];
          const labels = options
            ?.filter(
              option =>
                selectedValues.includes &&
                selectedValues.includes(option.value),
            )
            .map(option => option.label);

          return labels?.map(value => (
            <Chip
              key={`${field.id}-${value}-value-chip`}
              sx={{ mr: 1 }}
              label={value}
              color={'primary'}
            />
          ));
        }}
        onChange={onChange}
        error={error}
        helperText={
          <FieldHelperText
            description={description}
            helperText={helperText}
            error={error}
          />
        }
        sx={{
          ...sx,
          '& .MuiInputBase-root': {
            minHeight: '56px',
            background: isImported ? theme => theme.palette.grey[200] : null,
          },
        }}
      >
        {options?.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {isMultiSelect && (
              <Checkbox
                checked={
                  Array.isArray(value) && value.indexOf(option.value) > -1
                }
              />
            )}
            {option.label}
          </MenuItem>
        ))}
      </ClearableSelect>
    </>
  );
}
